/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Loading an existing event back into the wizard.
 *
 * The read is the mirror of the write: one request, ordered operations, with
 * `@ref.column` linking children to the parent that was read before them. The
 * result is turned back into exactly the form shape `eventFormReducer` produces,
 * so every step component works unchanged for Edit.
 */

import { initialEventForm } from './eventFormReducer';
import { utcToWallClock } from '../../utils/zonedDateTime';

/** Read refs, kept short — they travel in the payload. */
export const READ_REFS = {
  event: 'e',
  activities: 'a',
  route: 'route',
  venue: 'venue',
  pitStops: 'pit',
  platformRegistration: 'reg',
  externalRegistration: 'ext',
  sponsors: 'sponsor',
};

export function buildEventReadBatch(eventId, { organizationId, applicationId }) {
  const id = Number(eventId);
  return {
    organization_id: organizationId,
    application_id: applicationId,
    operations: [
      { ref: READ_REFS.event, table: 'event', where: { id } },
      { ref: READ_REFS.activities, table: 'event_activity', where: { event_id: `@${READ_REFS.event}.id` } },
      { ref: READ_REFS.route, table: 'event_activity_route', where: { event_activity_id: `@${READ_REFS.activities}.id` } },
      { ref: READ_REFS.venue, table: 'event_activity_venue', where: { event_activity_id: `@${READ_REFS.activities}.id` } },
      { ref: READ_REFS.pitStops, table: 'event_activity_pit_stop', where: { event_activity_id: `@${READ_REFS.activities}.id` } },
      { ref: READ_REFS.platformRegistration, table: 'event_platform_registration', where: { event_id: `@${READ_REFS.event}.id` } },
      { ref: READ_REFS.externalRegistration, table: 'event_external_registration', where: { event_id: `@${READ_REFS.event}.id` } },
      { ref: READ_REFS.sponsors, table: 'event_sponsor', where: { event_id: `@${READ_REFS.event}.id` } },
    ],
  };
}

/** Rows of one result block, or [] when that block failed or is absent. */
function rowsOf(results, ref) {
  const block = results?.[ref];
  if (!block || block.status !== 'ok' || !Array.isArray(block.data)) return [];
  return block.data;
}

/**
 * A stored datetime as the `YYYY-MM-DDTHH:MM` the input needs, read against the
 * event's own timezone.
 *
 * The column holds UTC, so it has to be turned back into the wall clock the
 * admin typed or an event created at 18:00 in Asia/Kolkata reopens the form at
 * 12:30. `utcToWallClock` is the exact inverse of the conversion `buildEventBatch`
 * applies on the way out, so loading an event and saving it untouched stores the
 * instant it started with.
 *
 * Without a usable zone it trims the value textually instead — what this form
 * did before zones were applied, so the field still renders rather than blanking.
 */
export function toDateTimeLocal(value, timeZone) {
  return utcToWallClock(value, timeZone);
}

const numberOrEmpty = (value) => (value === null || value === undefined ? '' : value);

function pointFrom(row, field) {
  const value = row?.[field];
  if (value && typeof value === 'object' && 'lat' in value) {
    return { lat: value.lat ?? '', lng: value.lng ?? '' };
  }
  return { lat: '', lng: '' };
}

export function hydrateEventForm(results) {
  const event = rowsOf(results, READ_REFS.event)[0];
  if (!event) return null;

  const routes = rowsOf(results, READ_REFS.route);
  const venues = rowsOf(results, READ_REFS.venue);
  const pitStops = rowsOf(results, READ_REFS.pitStops);

  const activities = [...rowsOf(results, READ_REFS.activities)]
    .sort((left, right) => (left.order_index ?? 0) - (right.order_index ?? 0))
    .map((activity) => {
      const route = routes.find((row) => row.event_activity_id === activity.id) || null;
      const venue = venues.find((row) => row.event_activity_id === activity.id) || null;
      return {
        id: activity.id,
        name: activity.name || '',
        description: activity.description || '',
        activity_type_id: numberOrEmpty(activity.activity_type_id),
        location_model: activity.location_model || 'ROUTE_BASED',
        route: route && {
          id: route.id,
          start_address: route.start_address || '',
          end_address: route.end_address || '',
          start_point: pointFrom(route, 'start_point'),
          end_point: pointFrom(route, 'end_point'),
          total_distance: numberOrEmpty(route.total_distance),
          distance_measuring_unit_id: numberOrEmpty(route.distance_measuring_unit_id),
          gpx_file_url: route.gpx_file_url || '',
        },
        venue: venue && {
          id: venue.id,
          venue_name: venue.venue_name || '',
          venue_address: venue.venue_address || '',
          venue_point: pointFrom(venue, 'venue_point'),
        },
        pit_stops: pitStops
          .filter((stop) => stop.event_activity_id === activity.id)
          .sort((left, right) => (left.order_index ?? 0) - (right.order_index ?? 0))
          .map((stop) => ({
            id: stop.id,
            name: stop.name || '',
            description: stop.description || '',
            point: pointFrom(stop, 'point'),
          })),
      };
    });

  // Every wall clock on this form is read against the event's own zone, never
  // the browser's — the same zone `buildEventBatch` wrote them with.
  const eventTimeZone = event.timezone;

  const platform = rowsOf(results, READ_REFS.platformRegistration)[0];
  const external = rowsOf(results, READ_REFS.externalRegistration)[0];

  return {
    ...initialEventForm(),
    id: event.id,
    title: event.title || '',
    slug: event.slug || '',
    description: event.description || '',
    // Both columns are nullable, so a row written before they existed reads
    // back NULL — coerced to '' here because the textarea is a controlled input.
    prerequisites: event.prerequisites || '',
    preparation_steps: event.preparation_steps || '',
    cover_image_url: event.cover_image_url || '',
    official_website_url: event.official_website_url || '',
    creator_type: event.creator_type || 'ORGANIZATION',
    creator_organization_id: numberOrEmpty(event.creator_organization_id),
    creator_user_id: numberOrEmpty(event.creator_user_id),
    organizer: event.organizer || 'PLATFORM',
    activity_mode: event.activity_mode || 'DEFAULT',
    location: event.location || '',
    location_state: event.location_state || '',
    location_country: event.location_country || '',
    location_pincode: event.location_pincode || '',
    timezone: event.timezone || '',
    start_datetime: toDateTimeLocal(event.start_datetime, eventTimeZone),
    end_datetime: toDateTimeLocal(event.end_datetime, eventTimeZone),
    registration_source: event.registration_source || 'PLATFORM',
    registration_type_id: numberOrEmpty(event.registration_type_id),
    is_registration_required: !!event.is_registration_required,
    is_payment_required: !!event.is_payment_required,
    // Only the two authorable states may be edited. An event the clock has
    // already moved to REGISTRATION_OPEN keeps that state until the clock
    // changes it again — the form must not offer to set it by hand.
    status: ['DRAFT', 'PUBLISHED'].includes(event.status) ? event.status : 'PUBLISHED',
    loaded_status: event.status,
    activities,
    sponsors: [...rowsOf(results, READ_REFS.sponsors)]
      .sort((left, right) => (left.order_index ?? 0) - (right.order_index ?? 0))
      .map((sponsor) => ({
        id: sponsor.id,
        name: sponsor.name || '',
        logo_url: sponsor.logo_url || '',
        cover_image: sponsor.cover_image || '',
        website_url: sponsor.website_url || '',
      })),
    platform_registration: platform ? {
      id: platform.id,
      registration_start_datetime: toDateTimeLocal(platform.registration_start_datetime, eventTimeZone),
      registration_end_datetime: toDateTimeLocal(platform.registration_end_datetime, eventTimeZone),
      max_participants: numberOrEmpty(platform.max_participants),
    } : null,
    external_registration: external ? {
      id: external.id,
      external_url: external.external_url || '',
      registration_start_date: toDateTimeLocal(external.registration_start_date, eventTimeZone),
      registration_end_date: toDateTimeLocal(external.registration_end_date, eventTimeZone),
    } : null,
  };
}
